import algoliasearch from 'algoliasearch/lite';
import isEmpty from 'lodash.isempty';
import React, { useCallback, useEffect, useState } from 'react';
import { InstantSearch } from 'react-instantsearch-dom';
import { useDispatch, useSelector } from 'react-redux';

import { useTranslation } from 'react-i18next';
import InfiniteHitsWithScrollToTop from '../components/InfiniteHitsWithScrollToTop/InfiniteHitsWithScrollToTop';
import NavigationBar from '../components/NavigationBar/NavigationBar';
import SortBy from '../components/SortBy/SortBy';

import { ReactComponent as FilterIcon } from '../assets/images/svg/filter.svg';
import Filter from '../components/Filter/Filter';
import VirtualFilter from '../components/Filter/VirtualFilters';
import Checkbox from '../components/InputFields/Checkbox';
import { ProductSearch } from '../components/InputFields/ProductSearch';
import BlockSectionLoader from '../components/Loader/BlockSectionLoader';
import FilterModal from '../components/Modal/FilterModal';
import MailInStorageWizard from '../components/Modals/MailInStorage/MailInStorageWizard';
import PriceTierModal from '../components/Modals/Prices/PriceTierModal';
import NagDashboardBanner from '../components/NagDashboard/NagDashboardBanner';
import MailInCta from '../components/Portfolio/MailInCta/MailInCta';
import Section from '../components/Section/Section';
import SellTradingContainer from '../components/Trading/SellTradingContainer';
import TradingContent from '../components/Trading/TradingContent';
import TradingFilter from '../components/Trading/TradingFilter';
import { TradingFilterButton } from '../components/Trading/TradingFilterButton';
import TradingHeader from '../components/Trading/TradingHeader';
import TradingSideBarContainer from '../components/Trading/TradingSideBarContainer';
import config from '../config';
import { ACCOUNT_DATA_LOADED } from '../constants/gtmEvents';
import { PORTFOLIO_PAGE } from '../constants/pages';
import {
  ADD_MAIL_IN_STORAGE_WIZARD,
  IS_ADD_MAIL_IN_WIZARD_OPEN,
  IS_INSTA_VAULT_PRODUCT,
  IS_PRODUCT_FRACTIONAL,
} from '../constants/sessionStorage';
import { COUNTDOWN_TIMER_INTERVAL_MS } from '../constants/timerConstants';
import { ACCOUNT_SETTINGS_LOADING } from '../store/actions/account/accountActionConstants';
import { fetchMyHoldingsCodes } from '../store/actions/portfolio/portfolioActions';
import { PRODUCT_PRICES_LOADING } from '../store/actions/products/productActionConstants';
import { fetchProductsWithPrices } from '../store/actions/products/productActions';
import { getCurrentAccount } from '../store/selectors/accountSelectors';
import { selectIsLoadingByActionType } from '../store/selectors/loadingSelectors';
import {
  selectMailInEnabled,
  selectSettings,
} from '../store/selectors/settingsSelectors';
import { accountTypeKeys, accountTypes } from '../util/enum/api/accountTypes';
import { PRODUCT_TYPE_FRACTIONAL } from '../util/enum/api/productTypes';
import { parseEnumType } from '../util/helpers/enumMappers';
import { removeFromSessionStorage } from '../util/helpers/sessionStorageHelper';
import { setWizardContent } from '../util/helpers/wizardHelpers';
import useGetPeriodicProductCodes from '../util/hooks/useGetPeriodicProductCodes';
import useGtmHook from '../util/hooks/useGtmHook';
import useWindowSize from '../util/hooks/useIsMobileHook';
import { useSessionStorageState } from '../util/hooks/useSessionStorageState';
import ComingSoonBanner from '../components/ComingSoon/ComingSoon';

const { apiKey, apiSecret, apiSellIndex } = config.algoliaSearch;
const searchClient = algoliasearch(apiKey, apiSecret);
const { brokerTheme } = config;

const SellPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isGS = brokerTheme === 'GoldSilver';
  const isHAA = brokerTheme === 'HardAssetsAlliance';
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMyHoldingsChecked, setIsMyHoldingsChecked] = useState(false);
  const [products, setProducts] = useState(null);
  const [isPriceTiersOpen, setIsPriceTiersOpen] = useState(false);
  const [priceTiersData, setPriceTiersData] = useState({});
  const windowSize = useWindowSize();
  const isMobile = windowSize.width < 1200;
  const isWidthL = windowSize.width > 768 && windowSize.width <= 1199.99;
  const isWidthS = windowSize.width <= 768;
  const account = useSelector(getCurrentAccount);
  const settings = useSelector(selectSettings);
  const isLoadingProducts = useSelector(
    selectIsLoadingByActionType(PRODUCT_PRICES_LOADING),
  );
  const isLoadingAccountSettings = useSelector(
    selectIsLoadingByActionType(ACCOUNT_SETTINGS_LOADING),
  );
  const [isFractionalProduct, setIsFractionalProduct] = useSessionStorageState(
    IS_PRODUCT_FRACTIONAL,
    false,
  );
  const [isInstaVaultProduct, setIsInstaVaultProduct] = useSessionStorageState(
    IS_INSTA_VAULT_PRODUCT,
    false,
  );
  const [searchState, setSearchState] = useState({});
  const [isLockedPrice, setIsLockedPrice] = useState(true);
  const mailInEnabled = useSelector(selectMailInEnabled);
  const isIRAAccount =
    parseEnumType(accountTypeKeys, account.AccountType) === accountTypes.IRA;
  const mailInAllowed = mailInEnabled && !isIRAAccount;
  const [mailInWizardOpen, setMailInWizardOpen] = useSessionStorageState(
    ADD_MAIL_IN_STORAGE_WIZARD,
    false,
  );
  const CoomingSoonBannerRequired = settings?.CoomingSoonBannerRequired;

  const myHoldingsChangeHandler = ({ target: { checked } }) =>
    setIsMyHoldingsChecked(checked);

  const priceTiersClickHandler = (product, location) => {
    setWizardContent(
      IS_PRODUCT_FRACTIONAL,
      product.ProductType === PRODUCT_TYPE_FRACTIONAL,
    );
    setIsFractionalProduct(product.ProductType === PRODUCT_TYPE_FRACTIONAL);
    const isInstavault =
      isGS &&
      (product.ProductCode === settings.InstavaultProductCodes[0] ||
        product.ProductCode === settings.InstavaultProductCodes[1]);
    setWizardContent(IS_INSTA_VAULT_PRODUCT, isInstavault);
    setIsInstaVaultProduct(isInstavault);
    setIsPriceTiersOpen(true);
    setPriceTiersData(location);
  };

  const [counterValue, setCounterValue] = useState(
    Math.floor(
      settings.PricingRefreshFrequencyMilliseconds /
        COUNTDOWN_TIMER_INTERVAL_MS,
    ),
  );
  const [unityValue, setUnityValue] = useState(false);

  const periodicCallback = useCallback((epoch) => {
    if (epoch?.data) {
      const [counter, unity] = epoch.data;
      setCounterValue((prevCounter) => prevCounter - prevCounter + counter);
      setUnityValue(unity);
    }
  }, []);

  useGetPeriodicProductCodes(
    'sell',
    account,
    'Sell',
    !!products || (!isEmpty(products) && products.length),
    settings?.PricingRefreshFrequencyMilliseconds,
    !isLockedPrice ? periodicCallback : null,
  );

  const { gtmScreenView, appGtmEvent } = useGtmHook();

  const getPrices = () => {
    dispatch(
      fetchProductsWithPrices({
        accountUid: account.AccountUid,
        side: 'Sell',
      }),
    );
  };

  const MailInCtaComponent = () => {
    if (mailInAllowed) {
      return (
        <>
          <MailInCta
            setInMailInStorageModalOpen={setMailInWizardOpen}
            account={account}
            alignment="start"
            isWidthS={isWidthS}
          />
          {mailInWizardOpen && (
            <MailInStorageWizard
              isOpen={mailInWizardOpen}
              onClose={() => {
                removeFromSessionStorage(IS_ADD_MAIL_IN_WIZARD_OPEN);
                setMailInWizardOpen(false);
              }}
            />
          )}
        </>
      );
    }

    return <></>;
  };

  useEffect(() => {
    gtmScreenView({
      path: window.location.pathname,
      title: 'Sell',
    });
    appGtmEvent(ACCOUNT_DATA_LOADED);
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!isEmpty(account) && account.AccountUid) {
      dispatch(
        fetchMyHoldingsCodes({
          accountUid: account.AccountUid,
        }),
      );
    }
  }, [isMyHoldingsChecked, account, account.AccountUid, dispatch]);

  useEffect(() => {
    if (!isEmpty(account) && account.AccountUid) {
      getPrices();
    }
  }, [account.AccountUid, dispatch]); // eslint-disable-line

  useEffect(() => {
    if (!settings || isEmpty(settings)) {
      return;
    }

    setIsLockedPrice(settings?.IsLockedPricingActive);
  }, [settings]);

  return (
    <>
      <NagDashboardBanner />
      {isHAA && CoomingSoonBannerRequired && <ComingSoonBanner />}
      <NavigationBar
        hasBack
        pageRoute={PORTFOLIO_PAGE}
        mainPage={t('header.navDropdownInvest')}
        subPage={t('header.linkSell')}
      />
      <Section>
        <SellTradingContainer>
          <InstantSearch
            indexName={apiSellIndex}
            searchClient={searchClient}
            searchState={searchState}
            onSearchStateChange={setSearchState}
          >
            {isMobile && <VirtualFilter />}
            {!isMobile && (
              <TradingSideBarContainer>
                <MailInCtaComponent />
                <TradingFilter data-cy="container-filters">
                  {!products ? (
                    <BlockSectionLoader isLoading />
                  ) : (
                    <Filter
                      products={products}
                      isForSell
                      myHoldingsChangeHandler={myHoldingsChangeHandler}
                      isMyHoldingsChecked={isMyHoldingsChecked}
                    />
                  )}
                </TradingFilter>
              </TradingSideBarContainer>
            )}
            <TradingContent data-cy="container-product-list">
              {isWidthS && <MailInCtaComponent />}
              {isWidthL && (
                <TradingSideBarContainer bpMd={isWidthL}>
                  <MailInCtaComponent />
                  <TradingHeader>
                    <div>
                      <SortBy
                        items={[
                          {
                            value: apiSellIndex,
                            label: t('sortBy.popularity'),
                            dataCy: 'menu-item-product-sort-popularity',
                          },
                          {
                            value: `${apiSellIndex}_prices_asc`,
                            label: t('sortBy.lowToHigh'),
                            dataCy: 'menu-item-product-sort-low-to-high',
                          },
                          {
                            value: `${apiSellIndex}_prices_desc`,
                            label: t('sortBy.highToLow'),
                            dataCy: 'menu-item-product-sort-high-to-low',
                          },
                        ]}
                        defaultRefinement={apiSellIndex}
                        getPrices={getPrices}
                      />
                      <ProductSearch />
                      <TradingFilterButton
                        onClick={() => {
                          setIsModalOpen(true);
                        }}
                      >
                        <FilterIcon />
                      </TradingFilterButton>
                    </div>

                    {isMobile && (
                      <Checkbox
                        variant="MyHoldingsCheckbox"
                        onChange={myHoldingsChangeHandler}
                        checked={isMyHoldingsChecked}
                      >
                        {t('portfolio.holdings.myHoldingsOnlyLabel')}
                      </Checkbox>
                    )}
                  </TradingHeader>
                </TradingSideBarContainer>
              )}
              {(!isMobile || isWidthS) && (
                <TradingHeader>
                  <div>
                    <SortBy
                      items={[
                        {
                          value: apiSellIndex,
                          label: t('sortBy.popularity'),
                          dataCy: 'menu-item-product-sort-popularity',
                        },
                        {
                          value: `${apiSellIndex}_prices_asc`,
                          label: t('sortBy.lowToHigh'),
                          dataCy: 'menu-item-product-sort-low-to-high',
                        },
                        {
                          value: `${apiSellIndex}_prices_desc`,
                          label: t('sortBy.highToLow'),
                          dataCy: 'menu-item-product-sort-high-to-low',
                        },
                      ]}
                      defaultRefinement={apiSellIndex}
                      getPrices={getPrices}
                    />
                    <ProductSearch />
                    <TradingFilterButton
                      onClick={() => {
                        setIsModalOpen(true);
                      }}
                    >
                      <FilterIcon />
                    </TradingFilterButton>
                  </div>

                  {isMobile && (
                    <Checkbox
                      variant="MyHoldingsCheckbox"
                      onChange={myHoldingsChangeHandler}
                      checked={isMyHoldingsChecked}
                    >
                      {t('portfolio.holdings.myHoldingsOnlyLabel')}
                    </Checkbox>
                  )}
                </TradingHeader>
              )}
              <InfiniteHitsWithScrollToTop
                isMyHoldingsChecked={isMyHoldingsChecked}
                priceTiersClickHandler={priceTiersClickHandler}
                page="sell"
                side="sell"
                setProducts={setProducts}
                products={products}
                isLoading={isLoadingProducts || isLoadingAccountSettings}
                isLockedPrice={isLockedPrice}
                counter={counterValue}
                unity={unityValue}
              />
              <PriceTierModal
                isOpen={isPriceTiersOpen}
                data={priceTiersData}
                account={account}
                tradingSide="Sell"
                handleClose={() => setIsPriceTiersOpen(false)}
                handleBack={() => setIsPriceTiersOpen(false)}
                isInstaVaultProduct={isInstaVaultProduct}
                isFractionalProduct={isFractionalProduct}
              />
            </TradingContent>
            {isMobile && (
              <FilterModal
                title="Filters"
                isOpen={isModalOpen && isMobile}
                close={setIsModalOpen}
                size="sm"
              >
                <SortBy
                  items={[
                    {
                      value: apiSellIndex,
                      label: t('sortBy.popularity'),
                      dataCy: 'menu-item-product-sort-popularity-mobile',
                    },
                    {
                      value: `${apiSellIndex}_prices_asc`,
                      label: t('sortBy.lowToHigh'),
                      dataCy: 'menu-item-product-sort-low-to-high-mobile',
                    },
                    {
                      value: `${apiSellIndex}_prices_desc`,
                      label: t('sortBy.highToLow'),
                      dataCy: 'menu-item-product-sort-high-to-low-mobile',
                    },
                  ]}
                  defaultRefinement={apiSellIndex}
                  getPrices={getPrices}
                />
                {isEmpty(products) ? (
                  <BlockSectionLoader isLoading />
                ) : (
                  <>
                    <InstantSearch
                      indexName={apiSellIndex}
                      searchClient={searchClient}
                      searchState={searchState}
                      onSearchStateChange={setSearchState}
                    >
                      <Filter
                        products={products}
                        myHoldingsChangeHandler={myHoldingsChangeHandler}
                        isMyHoldingsChecked={isMyHoldingsChecked}
                        close={setIsModalOpen}
                      />
                    </InstantSearch>
                  </>
                )}
              </FilterModal>
            )}
          </InstantSearch>
        </SellTradingContainer>
      </Section>
    </>
  );
};

export default SellPage;
